import { Box, Button, IconButton, Menu, MenuItem } from "@mui/joy";
import { CircularProgress } from "@mui/material";
import {
  DataGridPremium,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-premium";
import { Booklet } from "entities/booklet";
import { MusicianCell, MusicianHeader } from "hooks/Missions/Missions";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectedTagIDSelector,
  setFormOpen,
  setSelectedBookletID,
} from "reducers/rhapsody";
import { seasonUtilsSelector } from "reducers/v2/seasonControl";
import {
  useCreateBookletMutation,
  useDeleteBookletMutation,
} from "redux/booklet/bookletEndpoints";
import { useBooklets } from "redux/booklet/bookletHooks";
import { meSelector } from "selectors/me";

/**
 *
 * @returns {ReactElement} MusicianContracts page
 */
export function MusicianContracts() {
  const tagID = useSelector(selectedTagIDSelector);
  const [deleteBooklet] = useDeleteBookletMutation();
  const dispatch = useDispatch();

  const {
    missions,
    musicians,
    musiciansMap,
    seasonMercury,
    assignments,
    projects,
    projectsMap,
    activeMusicians,
    projectPieces,
    pieces,
  } = useSelector(seasonUtilsSelector);

  const filters = [];

  if (tagID) {
    filters.push({
      name: "booklets.tagID",
      comparison: "eq",
      value: tagID,
    });
  }

  const { booklets } = useBooklets({
    filters: JSON.stringify(filters),
  });

  const { booklets: snippets } = useBooklets({
    filters: JSON.stringify([
      {
        name: "booklets.snippet",
        comparison: "eq",
        value: true,
      },
    ]),
  });

  const rows =
    activeMusicians?.reduce((a, v) => {
      const item = { ...v };
      projects.forEach((p) => {
        item[p.id] = true;
      });
      a.push(item);
      return a;
    }, []) ?? [];

  const columns: any = [
    {
      field: "musicianID",
      sortable: false,
      renderHeader: () => <MusicianHeader />,
      width: 200,
      headerName: "Musician",
      valueGetter: (p) => musiciansMap[p.row.id]?.fullName() ?? "",
      renderCell: (p) => (
        <MusicianCell
          musician={musiciansMap[p.row.id]}
          mission={missions.find((m) => m.musicianID === p.row.id)}
        />
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      renderCell: (p) => {
        const musicianID = p.row.id;
        const _assignments = assignments.filter(
          (a) => a.musicianID === musicianID
        );
        const workSessions = [];
        _assignments.forEach((a) => {
          try {
            const w = JSON.parse(a.workSessionIDs);
            workSessions.push(...w);
          } catch (error) {}
        });
        return `Plays on ${_assignments.length} Project${
          _assignments.length > 1 ? "s" : ""
        } (${workSessions.length} Work Session${
          workSessions.length > 1 ? "s" : ""
        })`;
      },
    },
    {
      field: "contract",
      headerName: "Contract",
      align: "right",
      width: 150,
      renderCell: (p) => {
        const musicianID = p.row.id;
        const booklet = booklets.find((b) => b.musicianID === musicianID);
        if (booklet)
          return (
            <>
              {/* <IconButton
                color="danger"
                size="sm"
                onClick={() => deleteBooklet(booklet.id)}
              >
                <i className="fa-solid fa-trash"></i>
              </IconButton> */}
              <Button
                startDecorator={<i className="fa-solid fa-file-contract"></i>}
                size="sm"
                onClick={() => {
                  dispatch(setSelectedBookletID(booklet.id));
                  dispatch(setFormOpen({ isOpen: true, formID: "booklet" }));
                }}
              >
                Open
              </Button>
            </>
          );
        return (
          <GenerateContractButton musicianID={musicianID} snippets={snippets} />
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        minHeight: "auto",
        height: 0,
        position: "relative",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {musicians != undefined ? (
        <DataGridPremium
          components={{
            Toolbar: CustomToolbar,
          }}
          isCellEditable={(p) => {
            if (p.field !== "memo") return false;
            const mission = missions.find((m) => m.musicianID === p.row.id);
            const job = seasonMercury.jobs.find(
              (j) => j.uuid === mission?.mercuryJobID
            );
            return p.field === "memo" && job !== undefined;
          }}
          getCellClassName={(p) => {
            const classes: string[] = [];
            const projectID = parseInt(p.field);
            if (!isNaN(projectID) && projectID > 0) {
              if (projectsMap[projectID]?.archived) {
                classes.push("opacity");
              }
            }
            const mission = missions.find((m) => m.musicianID === p.row.id);
            const job = seasonMercury.jobs.find(
              (j) => j.uuid === mission?.mercuryJobID
            );

            if (p.field === "memo" && job === undefined) classes.push("lock");

            return classes.join(" ");
          }}
          getRowClassName={(row) => {
            return `musician_${row.id}`;
          }}
          sx={{ width: "calc(100vw - 40px)", maxWidth: "100%" }}
          disableColumnMenu
          disableColumnFilter
          columns={columns}
          initialState={{
            pinnedColumns: {
              right: ["contract"],
              left: ["musicianID"],
            },
          }}
          rows={rows}
        />
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
}

function CustomToolbar() {
  return (
    <Box
      className="header"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        p: 1,
        gap: 1,
        alignItems: "center",
      }}
    >
      <GridToolbarQuickFilter
        size="small"
        placeholder="Search Musician..."
        fullWidth
        sx={{ flexGrow: 1 }}
        variant="outlined"
      />
    </Box>
  );
}

function GenerateContractButton({
  musicianID,
  snippets,
}: {
  musicianID: number;
  snippets: Booklet[];
}) {
  const utils = useSelector(seasonUtilsSelector);
  const { musiciansMap, tag } = utils;
  const [loading, setLoading] = useState(false);
  const me = useSelector(meSelector);
  const [createBooklet] = useCreateBookletMutation();
  const tagID = useSelector(selectedTagIDSelector);
  const dispatch = useDispatch();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLSpanElement>();

  const generateContract = async (snippet?: Booklet) => {
    setLoading(true);
    const musician = musiciansMap[musicianID];
    console.log(snippet.contract(musician, utils));
    const booklet = await createBooklet({
      author: `${me.firstName} ${me.lastName}`,
      content: snippet ? snippet.contract(musician, utils) : "",
      musicianID,
      name: `${musiciansMap[musicianID].fullName()} - ${tag.name} Contract`,
      tagID,
    }).unwrap();
    setLoading(false);
    dispatch(setSelectedBookletID(booklet.id));
    dispatch(setFormOpen({ isOpen: true, formID: "booklet" }));
  };

  return (
    <>
      <Button
        loading={loading}
        onClick={(e) => {
          if (snippets.length > 0) {
            setMenuAnchorEl(e.currentTarget);
          } else {
            generateContract();
          }
        }}
        variant="soft"
        color="neutral"
        size="sm"
      >
        Generate
      </Button>
      <Menu
        size="sm"
        onClose={() => setMenuAnchorEl(null)}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
      >
        {snippets.map((s) => {
          return (
            <MenuItem
              key={s.id}
              onClick={() => {
                generateContract(s);
                setMenuAnchorEl(null);
              }}
              sx={{ gap: 2 }}
            >
              {s.name}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
